import { formatMerchantDate, type MerchantBadgeView } from "../view-model-formatters.js";
import { MISSING_VALUE_LABEL } from "../../shared/display-labels.js";
import type {
  AuditActionGroup,
  AuditActorContext,
  AuditEntry,
  AuditRowViewModel,
} from "./models.js";
import { safeSummaryValue } from "./models.js";

export function auditActionGroupBadge(actionGroup: AuditActionGroup): MerchantBadgeView {
  const tones: Record<AuditActionGroup, MerchantBadgeView["tone"]> = {
    INSTALLATION: "blue",
    ACCESS: "orange",
    STORE: "blue",
    POLICY: "green",
    COMPETITOR: "blue",
    MAPPING: "green",
    REQUEST: "gray",
    OFFER: "green",
    ADMIN_NOTE: "gray",
    SYSTEM_ADMIN: "orange",
  };
  return { tone: tones[actionGroup], label: titleCase(actionGroup) };
}

export function auditActorLabel(actor: AuditActorContext): string {
  return actor.displayName ?? actor.actorId ?? actorTypeLabel(actor.actorType);
}

export function auditTimestampLabel(entry: AuditEntry): string {
  return formatMerchantDate(entry.createdAt);
}

export function toAuditRow(entry: AuditEntry): AuditRowViewModel {
  const badge = auditActionGroupBadge(entry.actionGroup);
  return {
    id: entry.id,
    timestampLabel: auditTimestampLabel(entry),
    actorLabel: auditActorLabel(entry.actor),
    actionLabel: entry.actionLabel ?? actionLabel(entry.action),
    actionClassName: badgeClassName(badge.tone),
    entityTypeLabel: entityTypeLabel(entry.targetEntityType),
    entityLabel: entry.targetEntityLabel ?? entry.targetEntityId,
    safeSummary: safeSummary(entry),
    supportInvolved: entry.supportInvolved,
  };
}

export function actionLabel(action: string): string {
  return action.length === 0 ? MISSING_VALUE_LABEL : titleCase(action);
}

export function actorTypeLabel(actorType: string): string {
  if (actorType === "SHOPIFY") return "Platform";
  if (actorType === "SYSTEM") return "Price Rescue system";
  if (actorType === "SYSTEM_ADMIN") return "Authorized support";
  if (actorType === "MERCHANT_USER") return "Merchant user";
  return titleCase(actorType);
}

export function entityTypeLabel(entityType: string): string {
  if (entityType === "Request") return "Price-match request";
  if (entityType === "Policy") return "Pricing policy";
  return entityType.length === 0 ? MISSING_VALUE_LABEL : titleCase(entityType);
}

export function safeSummary(entry: AuditEntry): string {
  if (entry.safeSummary !== undefined && entry.safeSummary.length > 0) return entry.safeSummary;
  if (entry.message !== undefined && entry.message.length > 0) return entry.message;
  if (entry.reason !== undefined && entry.reason.length > 0) return entry.reason;
  const after = firstSummaryValue(entry.afterSummary);
  if (after !== undefined) return after;
  const before = firstSummaryValue(entry.beforeSummary);
  return before ?? "Audit event recorded";
}

function firstSummaryValue(summary: Record<string, unknown> | undefined): string | undefined {
  const first = Object.entries(summary ?? {})[0];
  if (first === undefined) return undefined;
  return `${titleCase(first[0])}: ${safeSummaryValue(first[1])}`;
}

function badgeClassName(tone: MerchantBadgeView["tone"]): string {
  if (tone === "green") return "b-green";
  if (tone === "orange") return "b-orange";
  if (tone === "blue") return "b-blue";
  if (tone === "red") return "b-red";
  if (tone === "purple") return "b-purple";
  return "b-gray";
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(
      /(^|_)([a-z])/g,
      (_match, separator: string, letter: string) =>
        `${separator === "_" ? " " : ""}${letter.toUpperCase()}`,
    );
}
